/**
 * 交互式输入对话框组件
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useChatStore } from '@/stores';

interface InteractiveDialogProps {
  onSubmit: (sessionId: string, input: string) => void;
  onCancel: (sessionId: string) => void;
}

export const InteractiveDialog: React.FC<InteractiveDialogProps> = ({ onSubmit, onCancel }) => {
  const { interactivePrompt, hideInteractivePrompt } = useChatStore();
  const [inputValue, setInputValue] = useState('');
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // 自动聚焦
  useEffect(() => {
    if (interactivePrompt && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [interactivePrompt]);
  
  // 提交
  const handleSubmit = useCallback(() => {
    if (!interactivePrompt) return;
    
    if (!inputValue.trim()) {
      setHasError(true);
      return;
    }
    
    onSubmit(interactivePrompt.sessionId, inputValue);
    setInputValue('');
    hideInteractivePrompt();
  }, [interactivePrompt, inputValue, onSubmit, hideInteractivePrompt]);
  
  // 取消
  const handleCancel = useCallback(() => {
    if (interactivePrompt) {
      onCancel(interactivePrompt.sessionId);
      hideInteractivePrompt();
    }
  }, [interactivePrompt, onCancel, hideInteractivePrompt]);
  
  // 键盘事件
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  }, [handleSubmit]);
  
  if (!interactivePrompt) return null;

  // 前端兜底：prompt 过长时判定为日志噪声，折叠显示
  // 真正的交互提示行通常很短（< 200 字符）
  const rawPrompt = interactivePrompt.prompt || '请输入';
  const isPromptOversized = rawPrompt.length > 200;
  const displayPrompt = isPromptOversized
    ? rawPrompt.substring(0, 200) + ` …（已截断，共 ${rawPrompt.length} 字符）`
    : rawPrompt;

  // 上下文输出过长时也截断
  const contextMaxLen = interactivePrompt.backgroundTaskId ? 1200 : 300;
  const rawContext = interactivePrompt.context || '';
  const displayContext = rawContext.length > contextMaxLen
    ? rawContext.substring(rawContext.length - contextMaxLen)
    : rawContext;

  return (
    <div className="sse-interactive-overlay">
      <div className="sse-interactive-dialog">
        <div className="interactive-input-header">
          <span className="interactive-input-icon">⌨️</span>
          <span className="interactive-input-title">
            {interactivePrompt.backgroundTaskId ? '后台任务需要输入' : '命令需要输入'}
          </span>
        </div>
        <div className="interactive-input-body">
          {displayContext && (
            <div className="interactive-prompt-context">
              <div className="context-label">上下文输出:</div>
              <div className="context-output">
                {displayContext}
              </div>
            </div>
          )}
          <div className="interactive-prompt">
            <div className="prompt-label">提示:</div>
            <div className="prompt-text">{displayPrompt}</div>
          </div>
          <div className="interactive-input-field">
            <input
              ref={inputRef}
              type="text"
              placeholder="输入内容..."
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setHasError(false);
              }}
              onKeyDown={handleKeyDown}
              className={hasError ? 'error' : ''}
              autoFocus
            />
            <button className="interactive-send-btn" onClick={handleSubmit}>
              <span className="dashicons dashicons-arrow-right-alt2"></span>
            </button>
          </div>
          {interactivePrompt.timeout && (
            <div className="interactive-timeout">
              请在 {Math.round(interactivePrompt.timeout / 1000)} 秒内输入
            </div>
          )}
        </div>
        <div className="interactive-input-actions">
          <button className="interactive-input-btn cancel" onClick={handleCancel}>
            <span className="dashicons dashicons-no-alt"></span> 取消命令
          </button>
        </div>
      </div>
    </div>
  );
};